import { useNavigate, useParams } from "react-router";
import {
  TimelineShowInfo,
  TimelineTitle,
  TimelineTitleBadge,
  TimelineWrapper,
  Wrapper,
} from "./InsightsTab.styles";

import { ChartCurveOutlined, InfoCircleOutlined } from "@gds-react/icons";
import { DeviceBase } from "../../../../generated-api/device";
import { useDevice } from "../../../../hooks/useDevice";
import { routes } from "../../../../utils/routeHelper";
import { DeviceTabTypeEnum } from "../../../../types/DeviceTabTypeEnum";
import { useMemo, useState } from "react";
import { useWindowResize } from "../../../../hooks/useWindowResize";
import { CoreTimelineSeries } from "@gf-core/widgets/types/CoreDeviceTimelineConfiguration";
import CoreDeviceTimeline from "@gf-core/widgets/widgets/CoreDeviceTimeline";
import CoreDeviceDetailViewer from "@gf-core/widgets/widgets/CoreDeviceDetailViewer";
import { CoreDeviceTimelineRangeEnum } from "@gf-core/widgets/types/CoreDeviceTimelineRangeEnum";
import { useTranslation } from "react-i18next";

interface InsightsTileProps {
  accountId?: string;
  installationId?: string;
  deviceId?: string;
  device: DeviceBase;
  title?: string;
  titleIsClickable?: boolean;
  width: "full" | "half";
}

function InsightsTile({
  accountId,
  installationId,
  device,
  title,
  titleIsClickable = true,
  width,
}: InsightsTileProps) {
  const [showDetailsModal, setShowDetailsModel] = useState(false);
  const navigate = useNavigate();
  const { width: screenWidth } = useWindowResize();
  const isMobile = useMemo(() => screenWidth < 1400, [screenWidth]);

  if (!installationId || !device.deviceTwinId) {
    return null;
  }

  return (
    <TimelineWrapper
      key={device.deviceTwinId}
      style={{
        width: isMobile ? "100%" : width === "half" ? "50%" : "100%",
      }}
    >
      <TimelineTitle $withHover={titleIsClickable}>
        <TimelineTitleBadge>
          <ChartCurveOutlined
            style={{ color: "white", width: 20, height: 20 }}
          />
        </TimelineTitleBadge>
        <span
          onClick={() => {
            if (!titleIsClickable) return;
            navigate(
              routes.device.set(
                accountId ?? "",
                installationId ?? "",
                device.deviceTwinId,
                DeviceTabTypeEnum.Overview,
              ),
            );
          }}
        >
          {title ?? device.displayName}
        </span>
        <TimelineShowInfo onClick={() => setShowDetailsModel(true)}>
          <InfoCircleOutlined style={{ width: 16, height: 16 }} />
        </TimelineShowInfo>
      </TimelineTitle>
      <CoreDeviceTimeline
        id={`connection-state-${installationId}-${device.deviceTwinId}`}
        groupId={installationId}
        installationId={installationId}
        showControlBar
        toggleKind={true}
        configuration={[
          {
            deviceTwinId: device.deviceTwinId,
            series: [
              {
                seriesName: CoreTimelineSeries.AnalogIn1,
                type: "line",
              },
              {
                seriesName: CoreTimelineSeries.AnalogIn2,
                type: "line",
              },
              {
                seriesName: CoreTimelineSeries.Level,
                type: "line",
              },
              {
                seriesName: CoreTimelineSeries.MediaTemperatureAverage,
                type: "line",
              },
              {
                seriesName: CoreTimelineSeries.MediumTemperatureAverage,
                type: "line",
              },
              {
                seriesName: CoreTimelineSeries.PowerInstant,
                type: "line",
              },
              {
                seriesName: CoreTimelineSeries.Pump1LatestCurrent,
                type: "line",
              },
              {
                seriesName: CoreTimelineSeries.Pump1RuntimeToday,
                type: "line",
              },
              {
                seriesName: CoreTimelineSeries.Pump2LatestCurrent,
                type: "line",
              },
              {
                seriesName: CoreTimelineSeries.Pump2RuntimeToday,
                type: "line",
              },
              {
                seriesName: CoreTimelineSeries.SpeedAverage,
                type: "line",
              },
            ],
          },
        ]}
      />
      <CoreDeviceDetailViewer
        show={showDetailsModal}
        onClose={() => setShowDetailsModel(false)}
        isModal
        deviceTwinId={device.deviceTwinId}
        installationId={installationId}
      />
    </TimelineWrapper>
  );
}

export default function InsightsTab() {
  const { t } = useTranslation();
  const { accountId, installationId, deviceId } = useParams();
  const { data: device } = useDevice(
    installationId ?? "",
    deviceId ?? "",
    true,
  );

  return (
    <Wrapper>
      {installationId &&
        device?.model &&
        (device?.model.$deviceType === "GdpCommunicationDevice" ||
          device?.model.$deviceType === "MoxaDevice") && (
          <>
            <TimelineWrapper style={{ maxWidth: "100%" }}>
              <TimelineTitle>
                <TimelineTitleBadge>
                  <ChartCurveOutlined
                    style={{ color: "white", width: 20, height: 20 }}
                  />
                </TimelineTitleBadge>
                <span>{t("tabs.insights.networkQuality")}</span>
              </TimelineTitle>
              <CoreDeviceTimeline
                id={`signal-quality-${installationId}-${deviceId}`}
                groupId={installationId}
                range={CoreDeviceTimelineRangeEnum.LAST_7_DAYS}
                installationId={installationId}
                showControlBar
                configuration={[
                  {
                    deviceTwinId: deviceId ?? "",
                    series: [
                      {
                        seriesName: CoreTimelineSeries.SignalQuality,
                        type: "line",
                      },
                    ],
                  },
                ]}
                listConfiguration={{
                  activeColumns: [
                    {
                      accessorKey: "time",
                      header: "Timestamp",
                    },
                    {
                      accessorKey: "SignalQuality",
                      header: "Network Quality",
                    },
                    {
                      accessorKey: "value",
                      header: "Raw Signal Strength",
                    },
                    {
                      accessorKey: "Operator",
                      header: "Operator",
                    },
                    {
                      accessorKey: "NetworkType",
                      header: "Network Type",
                    },
                    {
                      accessorKey: "AccessTechnology",
                      header: "Access Technology",
                    },
                    {
                      accessorKey: "RawLinkQuality",
                      header: "Bit Error Rate",
                    },
                  ],
                }}
              />
            </TimelineWrapper>

            <TimelineWrapper style={{ maxWidth: "100%" }}>
              <TimelineTitle>
                <TimelineTitleBadge>
                  <ChartCurveOutlined
                    style={{ color: "white", width: 20, height: 20 }}
                  />
                </TimelineTitleBadge>
                <span>{t("tabs.insights.connectivityHistory")}</span>
              </TimelineTitle>
              <CoreDeviceTimeline
                id={`signal-history-${installationId}-${deviceId}`}
                groupId={installationId}
                installationId={installationId}
                range={CoreDeviceTimelineRangeEnum.LAST_7_DAYS}
                showControlBar
                toggleKind
                configuration={[
                  {
                    deviceTwinId: deviceId ?? "",
                    series: [
                      {
                        seriesName: CoreTimelineSeries.ConnectionState,
                        type: "event",
                      },
                    ],
                  },
                ]}
                listConfiguration={{
                  activeColumns: [
                    {
                      accessorKey: "time",
                      header: "Timestamp",
                    },
                    {
                      accessorKey: "ConnectionState",
                      header: "Connection State",
                    },
                    {
                      accessorKey: "ConnectReason",
                      header: "Connection Reason",
                    },
                  ],
                }}
              />
            </TimelineWrapper>

            <TimelineWrapper style={{ maxWidth: "100%" }}>
              <TimelineTitle>
                <TimelineTitleBadge>
                  <ChartCurveOutlined
                    style={{ color: "white", width: 20, height: 20 }}
                  />
                </TimelineTitleBadge>
                <span>{t("tabs.insights.firmwareHistory")}</span>
              </TimelineTitle>
              <CoreDeviceTimeline
                id={`firmware-history-${installationId}-${deviceId}`}
                groupId={installationId}
                installationId={installationId}
                range={CoreDeviceTimelineRangeEnum.LAST_7_DAYS}
                showControlBar
                toggleKind={false}
                configuration={[
                  {
                    deviceTwinId: deviceId ?? "",
                    series: [
                      {
                        seriesName: CoreTimelineSeries.FirmwareUpdateState,
                        type: "event",
                      },
                    ],
                  },
                ]}
                listConfiguration={{
                  activeColumns: [
                    {
                      accessorKey: "time",
                      header: "Timestamp",
                    },
                    { accessorKey: "series", header: "Series Name" },
                    {
                      accessorKey: "value",
                      header: "Connection Value",
                    },
                  ],
                }}
              />
            </TimelineWrapper>
          </>
        )}
      {installationId &&
        device?.model &&
        (device?.model.$deviceType === "ModbusRtu" ||
          device?.model.$deviceType === "GeniProDevice") && (
          <InsightsTile
            device={device?.model}
            accountId={accountId}
            installationId={installationId}
            titleIsClickable={false}
            width="full"
          />
        )}
    </Wrapper>
  );
}
